import React, { useContext } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import CustomDialog, { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomButton from "../../../components/Button/CustomButton";
import CustomAlert from "../../../components/Alert/CustomAlert";
import CustomTextField from "../../../components/Input/CustomTextField";
import { tesaBlue, tesaDarkRedError } from "../../../styles/colors";
import ProductApi from "../../../api/services/ProductApi";
import useEnv from "../../../hooks/env";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    invalidProdName: {
      color: tesaDarkRedError,
    },
    link: {
      cursor: "pointer",
      color: tesaBlue,
    },
    marginBottom: {
      marginBottom: theme.spacing(4),
    },
    marginTop: {
      marginTop: theme.spacing(5),
    },
    marginRight: {
      marginRight: theme.spacing(3),
    },
  }),
);

interface CompetitorCompanyPayload {
  company: string;
}

const CompetitorCompanyCreateDialog = (
  props: CustomDialogProps & {
    onClose: (doUpdate: boolean) => void;
  },
) => {
  const classes = useStyles();
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const { open, onClose } = props;
  const { api } = useEnv();
  const productApi = new ProductApi(api);

  const yupValidationSchema = Yup.object().shape({
    company: Yup.string().required("Please enter a name"),
  });

  const formatPayloadForApi = (payload: CompetitorCompanyPayload): any => {
    return {
      name: payload.company,
    };
  };

  return (
    <CustomDialog fullWidth maxWidth="sm" onClose={() => onClose(false)} open={open}>
      <h2>CREATE COMPETITOR COMPANY</h2>
      <CustomAlert
        className={classes.marginBottom}
        severity="info"
        text="You are about to create a company in the database for all users. 
        Make sure that the company is not already created and that the name is accurate."
      />
      <Formik<CompetitorCompanyPayload>
        initialValues={{
          company: "",
        }}
        onSubmit={(values, { setSubmitting, setFieldError }) => {
          const formattedPayload = formatPayloadForApi(values);
          productApi
            .createNewCompany(formattedPayload)
            .then(() => {
              showSnackbar("Competitor company created", "success");
              onClose(true);
            })
            .catch((e) => {
              setSubmitting(false);
              setFieldError("company", "This company already exists.");
              handleHttpError(e);
            });
        }}
        validationSchema={yupValidationSchema}
      >
        {(formikProps: FormikProps<CompetitorCompanyPayload>): JSX.Element => {
          // formik inner jsx form
          const { errors, handleBlur, handleChange, isSubmitting, touched, values } = formikProps;
          return (
            <Form>
              <Grid container justify="flex-start" direction="row" spacing={4}>
                <Grid item xs={12}>
                  <CustomTextField
                    autoFocus
                    fullWidth
                    mandatory
                    name="company"
                    id="company"
                    label="Company"
                    value={values.company}
                    type="text"
                    helperText={
                      errors.company && touched.company
                        ? errors.company
                        : "Full name of the producing company incl. corporate form (SE, GmbH, ..)"
                    }
                    error={!!(errors.company && touched.company)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
              </Grid>
              <Grid className={classes.marginTop} container direction="row" justify="flex-end">
                <CustomButton
                  disabled={isSubmitting}
                  text="Cancel"
                  className={classes.marginRight}
                  onClick={() => onClose(false)}
                />
                <CustomButton disabled={isSubmitting} primary text="Create" type="submit" />
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </CustomDialog>
  );
};

export default CompetitorCompanyCreateDialog;
